import * as React from "react";
import Api, { APIs } from "../../../Api";

export interface IPledgeStatus {
  id: number;
  development_partner: string;
  financing_type: string;
  pledged_amount_million_usd: number | null;
  amount_released_million_usd: number | null;
  disbursement: number | null;
  disbursement_date: string | null;
  projected_disbursement: number | null;
  projected_fy_2425_1q: number | null;
  projection_date: string | null;

  [key: string]: any;
}

const PledgeStatus = () => {
  const [status, setStatus] = React.useState<IPledgeStatus[]>([]);
  const [notes, setNotes] = React.useState<string[]>([]);
  const [maxDisbursementDate, setMaxDisbursementDate] = React.useState<
    Date | undefined
  >(undefined);
  const [maxProjectionYear, setMaxProjectionYear] = React.useState<string>("");
  const [maxProjectionDate, setMaxProjectionDate] = React.useState<
    Date | undefined
  >(undefined);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(null);

  React.useEffect(() => {
    Api.get(APIs.MoPD_PLEDGE_STATUS).then((payload) => {
      // console.log("pledge_status", payload);
      if (payload) {
        setStatus(payload.data);
        setNotes(payload.notes);
        setMaxDisbursementDate(findMaxDate(payload.data, "disbursement_date"));
        setMaxProjectionDate(findMaxDate(payload.data, "projection_date"));
        setMaxProjectionYear(
          findProjectionFinancialYear(payload.data, "projection_date")
        );
      }
    });
  }, []);

  const findMaxDate = (
    data: IPledgeStatus[],
    key: keyof IPledgeStatus
  ): Date | undefined => {
    const dates = data.map((item) => new Date(item[key] as string).getTime());

    if (!dates.length) return undefined;
    return new Date(Math.max(...dates));
  };
  const findProjectionFinancialYear = (
    data: IPledgeStatus[],
    key: keyof IPledgeStatus
  ): string => {
    const dates: number[] = data.map((item) =>
      new Date(item[key] as string).getTime()
    );

    if (!dates.length) return "";

    const maxDate = new Date(Math.max(...dates));
    const year = maxDate.getFullYear();
    const month = maxDate.getMonth();

    // Financial year calculation (assuming FY starts from April)
    const financialYearStart = month >= 7 ? year : year - 1;
    const financialYearEnd = financialYearStart + 1;

    return `FY ${financialYearStart}-${financialYearEnd.toString().slice(-2)}`;
  };

  const groupByFinancingType = (data: IPledgeStatus[]) => {
    return data.reduce((acc: Record<string, IPledgeStatus[]>, item) => {
      if (!acc[item.financing_type]) {
        acc[item.financing_type] = [];
      }
      acc[item.financing_type].push(item);
      return acc;
    }, {});
  };

  const groupedData = groupByFinancingType(status);

  const calculateSum = (items: IPledgeStatus[], key: keyof IPledgeStatus) => {
    return items.reduce((sum, item) => sum + (item[key] ?? 0), 0); // Use nullish coalescing operator to handle null values
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#333", // Darker background color for header
    color: "#fff", // White text color
    fontWeight: "bold",
  };

  const tdStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "center",
  };

  const headerRowStyle: React.CSSProperties = {
    fontWeight: "bold",
    backgroundColor: "#f4f4f4",
  };

  const totalRowStyle: React.CSSProperties = {
    fontWeight: "bold",
    backgroundColor: "#000", // Black background color
    color: "#fff", // White text color
  };

  // Calculate the total sums for each column across all groups
  const totalPledgedAmount = calculateSum(status, "pledged_amount_million_usd");
  const totalAmountsRealized = calculateSum(
    status,
    "amount_released_million_usd"
  );
  const totalDisbursementActual = calculateSum(status, "disbursement");
  const totalProjectedDisbursement = calculateSum(
    status,
    "projected_disbursement"
  );
  const totalProjectedDisbursement1Q = calculateSum(
    status,
    "projected_fy_2425_1q"
  );

  // Check if all notes are null or empty
  const areAllNotesNull = notes.every(
    (note) => note === null || note.trim() === ""
  );

  // const getItemById = (id: number): IPledgeStatus | undefined => {
  //     return status.find(item => item.id === id);
  // };
  const handleRowDoubleClick = (id: number) => {
    setSelectedRowId(id);
    // console.log("Row double-clicked:", getItemById(id)); // Handle additional actions on double-click here
  };
  const rowStyle = (id: number): React.CSSProperties => ({
    backgroundColor: id === selectedRowId ? "#e0f7fa" : "transparent",
    cursor: "pointer",
  });
  const add1stQuarterInfo: boolean = false;
  return (
    <div>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th rowSpan={2} style={thStyle}>
              S/No
            </th>
            <th rowSpan={2} style={thStyle}>
              Development Partner
            </th>
            <th rowSpan={2} style={thStyle}>
              Pledged Amount (US$ Mn)
            </th>
            <th rowSpan={2} style={thStyle}>
              Amounts Realized/Committed (US$ Mn)
            </th>
            <th style={thStyle} colSpan={add1stQuarterInfo ? 3 : 2}>
              Disbursement (US$ Mn)
            </th>
          </tr>
          <tr>
            <th style={thStyle}>
              Actual <br />
              {/*Upto October 2024*/}
              Upto{" "}
              {maxDisbursementDate?.toLocaleString("default", {
                month: "short",
              })}{" "}
              {maxDisbursementDate?.getFullYear()}
            </th>
            <th style={thStyle}>
              Projected <br />
              {/*{maxProjectionYear}*/}
              Upto{" "}
              {maxProjectionDate?.toLocaleString("default", {
                month: "short",
              })}{" "}
              {maxProjectionDate?.getFullYear()}
              {/*{maxDisbursementDate}*/}
            </th>
            {add1stQuarterInfo ? (
              <th style={thStyle}>
                1st Q <br />
                {maxProjectionYear}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {Object.entries(groupedData).map(([type, items], typeIndex) => (
            <React.Fragment key={typeIndex}>
              <tr>
                <td colSpan={7} style={headerRowStyle}>
                  {type}
                </td>
              </tr>
              {items.map((item, index) => (
                <tr
                  key={item.id}
                  onDoubleClick={() => handleRowDoubleClick(item.id)}
                  style={rowStyle(item.id)}
                >
                  <td style={tdStyle}>{index + 1}</td>
                  <td style={tdStyle}>{item.development_partner}</td>
                  <td style={tdStyle}>
                    {item.pledged_amount_million_usd?.toLocaleString() || "N/A"}
                  </td>
                  <td style={tdStyle}>
                    {item.amount_released_million_usd?.toLocaleString() ||
                      "N/A"}
                  </td>
                  <td style={tdStyle}>
                    {item.disbursement?.toLocaleString() || "N/A"}
                  </td>
                  <td style={tdStyle}>
                    {item.projected_disbursement?.toLocaleString() || "N/A"}
                  </td>
                  {add1stQuarterInfo ? (
                    <td style={tdStyle}>
                      {item.projected_fy_2425_1q?.toLocaleString() || "N/A"}
                    </td>
                  ) : null}
                </tr>
              ))}
              {items.length > 1 && (
                <tr style={{ fontWeight: "bold" }}>
                  <td colSpan={2} style={{ ...tdStyle, fontWeight: "bold" }}>
                    Total for {type}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "bold",
                    }}
                  >
                    {calculateSum(
                      items,
                      "pledged_amount_million_usd"
                    ).toLocaleString()}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "bold",
                    }}
                  >
                    {calculateSum(
                      items,
                      "amount_released_million_usd"
                    ).toLocaleString()}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "bold",
                    }}
                  >
                    {calculateSum(items, "disbursement").toLocaleString()}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: "bold",
                    }}
                  >
                    {calculateSum(
                      items,
                      "projected_disbursement"
                    ).toLocaleString()}
                  </td>
                  {add1stQuarterInfo ? (
                    <td
                      style={{
                        ...tdStyle,
                        fontWeight: "bold",
                      }}
                    >
                      {calculateSum(
                        items,
                        "projected_fy_2425_1q"
                      ).toLocaleString()}
                    </td>
                  ) : null}
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={totalRowStyle}>
            <td
              colSpan={2}
              style={{
                ...tdStyle,
                color: "#fff",
                fontWeight: "bold",
                textAlign: "right",
              }}
            >
              Grand Total
            </td>
            <td
              style={{
                ...tdStyle,
                color: "#fff",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {totalPledgedAmount.toLocaleString()}
            </td>
            <td
              style={{
                ...tdStyle,
                color: "#fff",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {totalAmountsRealized.toLocaleString()}
            </td>
            <td
              style={{
                ...tdStyle,
                color: "#fff",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {totalDisbursementActual.toLocaleString()}
            </td>
            <td
              style={{
                ...tdStyle,
                color: "#fff",
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {totalProjectedDisbursement.toLocaleString()}
            </td>
            {add1stQuarterInfo ? (
              <td
                style={{
                  ...tdStyle,
                  color: "#fff",
                  fontWeight: "bold",
                  textAlign: "center",
                }}
              >
                {totalProjectedDisbursement1Q.toLocaleString()}
              </td>
            ) : null}
          </tr>
        </tfoot>
      </table>
      {!areAllNotesNull && (
        <div className="notes">
          {notes.map((note, index) => (
            <p key={index}>{note}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default PledgeStatus;
